// Прошивки RTS-панелей: загрузка образов и их список. Проверяет образ
// (размер, CRC, описатель) и хранит его Шанырак; здесь — файл туда и то, что
// он разобрал из описателя. Запуск обновления — у панели на странице
// «Устройства». Раздел только у админа с правом на прошивки.

import { useEffect, useRef, useState, type FormEvent } from "react";
import { gql } from "../lib/gql";
import {
  FIRMWARE_IMAGES_QUERY,
  failureText,
  formatDateTime,
  hwRange,
  loadFirmwareAccess,
  uploadFirmwareImage,
  type FirmwareImage,
} from "../lib/firmware";

const IMAGE_SIZE = 991232;

export function FirmwarePage() {
  const [access, setAccess] = useState<boolean | null>(null);
  const [images, setImages] = useState<FirmwareImage[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string; image?: FirmwareImage | null } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  async function loadImages() {
    try {
      const d = await gql<{ adminFirmwareImages: FirmwareImage[] }>(FIRMWARE_IMAGES_QUERY);
      setImages(d.adminFirmwareImages);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }

  useEffect(() => {
    loadFirmwareAccess().then((ok) => {
      setAccess(ok);
      if (ok) void loadImages();
    });
  }, []);

  async function onUpload(e: FormEvent) {
    e.preventDefault();
    if (!file || uploading) return;
    setUploading(true);
    setResult(null);
    try {
      const r = await uploadFirmwareImage(file);
      if (r.success) {
        setResult({ ok: true, text: "Образ загружен.", image: r.image });
        setFile(null);
        if (fileRef.current) fileRef.current.value = "";
        await loadImages();
      } else {
        setResult({ ok: false, text: failureText(r), image: r.image });
      }
    } catch (err) {
      setResult({ ok: false, text: err instanceof Error ? err.message : String(err) });
    } finally {
      setUploading(false);
    }
  }

  if (access === null) return <div className="muted">Загружаем…</div>;
  if (!access) {
    return (
      <div className="card">
        <h2 style={{ marginTop: 0 }}>Прошивки</h2>
        <p className="muted">
          Раздел доступен только учётке с правом на прошивки. Право выдаётся командой seed_admin в поде
          evimiz-core, через Sales его не выдать.
        </p>
      </div>
    );
  }

  return (
    <div>
      <h2>Прошивки RTS-панелей</h2>

      <form className="card" onSubmit={onUpload} style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Загрузить образ</h3>
        <p className="muted" style={{ marginTop: 0, fontSize: 13 }}>
          Образ .bin ровно {IMAGE_SIZE} Б с описателем. Шанырак проверит размер, контрольную сумму и
          описатель и покажет версию, вид, МК и диапазон железа. Образ без описателя не принимается.
          Повторная загрузка того же файла ничего не меняет.
        </p>
        <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input
            ref={fileRef}
            type="file"
            accept=".bin,application/octet-stream"
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setResult(null);
            }}
            disabled={uploading}
          />
          <button type="submit" disabled={!file || uploading}>
            {uploading ? "Загружаем…" : "Загрузить"}
          </button>
        </div>
        {file && file.size !== IMAGE_SIZE && (
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Файл {file.size} Б, а образ весит {IMAGE_SIZE} Б — Шанырак его, скорее всего, отклонит.
          </div>
        )}
        {result && (
          <div
            className={result.ok ? "muted" : "error-banner"}
            style={{ marginTop: 12, fontSize: 13, color: result.ok ? "#22c55e" : undefined }}
          >
            {result.text}
            {result.image && (
              <div style={{ marginTop: 6, color: "var(--text)" }}>
                {result.image.name}: версия {result.image.version}, {result.image.kind === "DEV" ? "dev-сборка" : "выпуск"},
                МК {result.image.mcu}, hw {hwRange(result.image)}, git {result.image.gitSha}, CRC {result.image.crc}
              </div>
            )}
          </div>
        )}
      </form>

      <div className="card">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <h3 style={{ margin: 0 }}>Образы</h3>
          <button className="secondary" onClick={() => void loadImages()}>
            Обновить
          </button>
        </div>
        {loadError && <div className="error-banner" style={{ marginTop: 12 }}>{loadError}</div>}
        {images.length === 0 && !loadError ? (
          <div className="muted" style={{ marginTop: 12 }}>Образов пока нет.</div>
        ) : (
          <table style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Версия</th>
                <th>Вид</th>
                <th>МК</th>
                <th>hw</th>
                <th>git</th>
                <th>CRC</th>
                <th>Загружен</th>
              </tr>
            </thead>
            <tbody>
              {images.map((img) => (
                <tr key={img.id}>
                  <td>
                    <div style={{ fontFamily: "monospace", fontWeight: 600 }}>{img.version}</div>
                    <div className="muted" style={{ fontSize: 12 }}>{img.path}</div>
                  </td>
                  <td>{img.kind === "DEV" ? "dev — только стенд" : "выпуск"}</td>
                  <td className="muted">{img.mcu}</td>
                  <td style={{ fontFamily: "monospace" }}>{hwRange(img)}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{img.gitSha}</td>
                  <td style={{ fontFamily: "monospace", fontSize: 12 }}>{img.crc}</td>
                  <td className="muted" style={{ fontSize: 12 }}>
                    {formatDateTime(img.uploadedAt)}
                    {img.uploadedBy && <div>{img.uploadedBy}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
